import React, { Component } from 'react';
import gql from 'graphql-tag';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import memoizeOne from 'memoize-one';
import PropTypes from 'prop-types';
import { compose, graphql } from 'react-apollo';
import ListGroup from 'react-bootstrap/ListGroup';
import ListGroupItem from 'react-bootstrap/ListGroupItem';
import { Helmet } from 'react-helmet';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import 'swiper/swiper-bundle';
import { register } from 'swiper/swiper-element';
import A11yFontIcon from 'components/A11yFontIcon';
import A11ySvg from 'components/A11ySvg';
import DebouncedTextInput from 'components/DebouncedTextInput';
import GameCard from 'components/GameCard';
import PageTitleBar from 'components/PageTitleBar';
import TextButton from 'components/TextButton';
import {
  CV_WEB_GAME_LAYOUT_UPDATE_ENABLED,
  LG_BREAKPOINT_AND_UP
} from 'utils/constants';
import { orderGamesByHistory } from 'utils/gameHistory';
import {
  GET_GAMES_QUERY,
  SUBSCRIPTION_GAME_UDPATES
} from 'utils/graphql/gamingQueries';
import { QUERY_GET_REMOTE_CONFIG_ENTRY } from 'utils/hook/query/useRemoteConfigEntry';
import {
  getGameFavorites,
  getGameSortBy,
  setGameFavorites,
  setGameSortBy
} from 'utils/localStorage';
import { errorShape } from 'utils/shapes';
import withMediaQuery from 'utils/withMediaQuery';
import { ReactComponent as ExitIcon } from 'resources/cross.svg';
import GamesIcon from 'resources/FeatureIcons/Button-Games.svg';
import GamePageNavigationBar from './GamePageNavigationBar';
import GamePageTitle from './GamePageTitle';
import { promoteGamesInList } from './promoteGamesInList';
import { alphabeticalSort } from './utils';
import IconFilter from './Assets/Icon_Filter.png';
import IconSearch from './Assets/Icon_Search.png';
import leftArrow from './Assets/Slots_Arrow_Left.png';
import rightArrow from './Assets/Slots_Arrow_Right.png';
import SelectedFilterCheck from './Assets/Button-Check-selected.png';
import UnselectedFilterCheck from './Assets/Button-Check-unselected.png';
import './GameSlotsPage.css';

const SLIDER_WIDTH = 700;
const FRAME_DURATION_MS = 70;
const HIGH_SPEED_TRIGGER = 12;
const LUDICROUS_SPEED_TRIGGER = 20;

class GameSlotsPage extends Component {
  constructor(props) {
    super(props);
    this.gameSortingOptions = [
      {
        name: 'Newly Released',
        sort: games => this.orderByNewlyReleasedMemo(games)
      },
      {
        name: 'Favorites',
        sort: games => this.orderByFavoritesMemo(games, getGameFavorites())
      },
      {
        name: 'Recently Played',
        sort: games => this.orderByRecentlyPlayedMemo(games)
      },
      {
        name: 'Alphabetical',
        sort: games => this.orderByAlphabeticalMemo(games)
      }
    ];

    const sortbyName = getGameSortBy();
    const orderMethod =
      (sortbyName &&
        this.gameSortingOptions.find(sort => sort.name === sortbyName)) ||
      this.gameSortingOptions[0];
    this.state = {
      gameCarousel: [],
      orderMethod: orderMethod,
      active: 0,
      targetActive: 0,
      direction: 1,
      sliderPosition: 0,
      showSortOptions: false,
      searchTerm: '',
      searchFoundList: null,
      showSearchBox: false,
      forceSearchClosed: 0,
      disableClick: true,
      swiperInit: false,
      swiper: null
    };

    this.orderByAlphabeticalMemo = memoizeOne(orderByAlphabetical);
    this.orderByFavoritesMemo = memoizeOne(
      orderByFavorites,
      ([games, favorites], [prevGames, prevFavorites]) =>
        games === prevGames && isEqual(favorites, prevFavorites)
    );
    this.orderByNewlyReleasedMemo = memoizeOne(orderByNewlyReleased);
    this.orderByRecentlyPlayedMemo = memoizeOne(orderByRecentlyPlayed);
    this.handleSortSelection = this.handleSortSelection.bind(this);
    this.addGameToFavorites = this.addGameToFavorites.bind(this);
    this.removeGameFromFavorites = this.removeGameFromFavorites.bind(this);
    this.showSortOptions = this.showSortOptions.bind(this);
    this.hideSortOptions = this.hideSortOptions.bind(this);
    this.updateActive = this.updateActive.bind(this);
    this.forceUpdate = this.forceUpdate.bind(this);
    this.showSearch = this.showSearch.bind(this);
    this.onHideSearch = this.onHideSearch.bind(this);
    this.moveRight = this.moveRight.bind(this);
    this.moveLeft = this.moveLeft.bind(this);
    this.onChange = this.onChange.bind(this);
    this.searchNotFound = this.searchNotFound.bind(this);
    this.handleSearchGame = this.handleSearchGame.bind(this);
    this.handleClearSearch = this.handleClearSearch.bind(this);
    this.unFreezeClick = this.unFreezeClick.bind(this);
    this.frameRequest = undefined;
    this.frameLastTimestamp = null;
  }

  componentDidMount() {
    this.unsubscribeFromGameUpdates = this.props.subscribeToGameUpdates();
    this.unsubscribeFromRemoteConfig = this.props.subscribeToNewData();
    this.frameRequest = requestAnimationFrame(this.updateActive);
    this.unFreezeClick();

    register();
  }

  componentDidUpdate(prevProps, { orderMethod, searchTerm }) {
    const swiperEl = document.querySelector('swiper-container');
    if (swiperEl && !this.state.swiperInit) {
      Object.assign(swiperEl, {
        scrollbar: {
          el: '.slider-container',
          draggable: true
        },
        navigation: {
          nextEl: '.btn-right',
          prevEl: '.btn-left'
        },
        centeredSlides: true,
        loop: false,
        slidesPerView: 7,
        mousewheel: true,
        keyboard: true,
        effect: 'creative',
        observer: true,
        creativeEffect: {
          limitProgress: 3,
          perspective: false,
          prev: {
            translate: ['-50%', 0, -200],
            rotate: [20, 0, 0],
            opacity: 0.6
          },
          next: {
            translate: ['50%', 0, -200],
            rotate: [20, 0, 0],
            opacity: 0.6
          }
        },
        breakpoints: {
          1024: {
            slidesPerView: 4
          }
        },
        on: {
          slideChange: ({ activeIndex, slides }) => {
            const sliderEl = document.querySelector('.slider');
            sliderEl?.setAttribute(
              'value',
              (activeIndex / (slides.length ?? 1)) * SLIDER_WIDTH
            );
          }
        }
      });
      swiperEl.initialize();
      this.setState(
        {
          swiperInit: true,
          swiper: swiperEl?.swiper
        },
        () => {
          const sliderEl = document.querySelector('.slider');
          sliderEl?.setAttribute('value', this.state.sliderPosition);
        }
      );
    }

    if (swiperEl && this.state.swiperInit) {
      swiperEl.swiper.update();
      const orderMethodChanged =
        orderMethod?.name !== this.state.orderMethod?.name;
      const searchTermChangedAndResultsFound =
        searchTerm !== this.state.searchTerm &&
        this.state.searchFoundList.length > 0;

      if (orderMethodChanged || searchTermChangedAndResultsFound) {
        swiperEl.style.visibility = 'hidden';
        swiperEl.style.opacity = 0;
        swiperEl.swiper?.slideTo(0);
        setTimeout(() => {
          swiperEl.swiper?.slidePrev();
          setTimeout(() => {
            swiperEl.style.visibility = 'visible';
            swiperEl.swiper?.slideNext();
            swiperEl.style.opacity = 1;
          }, 500);
        }, 500);
      }
    }
  }

  componentWillUnmount() {
    this.unsubscribeFromGameUpdates();
    this.unsubscribeFromGameUpdates = () => {};
    this.unsubscribeFromRemoteConfig();
    this.unsubscribeFromRemoteConfig = () => {};
    cancelAnimationFrame(this.frameRequest);
    clearTimeout(this.enableClick);
    this.state.swiper?.destroy();
  }

  unFreezeClick() {
    this.enableClick = setTimeout(() => {
      this.setState({ disableClick: false });
    }, 2000);
  }

  showSortOptions() {
    this.setState({ showSortOptions: true });
    this.onHideSearch();
  }

  hideSortOptions() {
    this.setState({ showSortOptions: false });
  }

  showSearch() {
    this.setState({ showSearchBox: true });
    this.hideSortOptions();
  }

  onHideSearch() {
    this.setState({ showSearchBox: false });
  }

  handleSearchGame(searchTerm) {
    this.setState({ searchTerm });
  }

  handleClearSearch() {
    this.setState({ searchFoundList: null, searchTerm: '' });
  }

  handleSortSelection(selection) {
    const swiper = this.state.swiper;

    setGameSortBy(selection && selection.name);
    this.setState({
      orderMethod: selection,
      active: 0,
      targetActive: 0,
      direction: 0,
      sliderPosition: 0,
      searchTerm: '',
      searchFoundList: null
    });
    this.hideSortOptions();
    const currentSlides = swiper?.slides;
    swiper?.slideTo(currentSlides.length / 2);
  }

  getTargetDistance() {
    const gameCount = this.props.data.games.games.length;
    const { targetActive, active, direction } = this.state;
    return (
      Math.abs(
        direction > 0
          ? targetActive + gameCount - active
          : targetActive - gameCount - active
      ) % gameCount
    );
  }

  addGameToFavorites(game) {
    let favoriteGamesList = getGameFavorites();
    const id = game.id;
    const duplicateIndex = favoriteGamesList.findIndex(gameId => gameId === id);
    if (duplicateIndex !== -1) {
      favoriteGamesList.splice(duplicateIndex, 1);
    }
    favoriteGamesList.push(id);
    setGameFavorites(favoriteGamesList);
  }

  removeGameFromFavorites(game) {
    let favoriteGamesList = getGameFavorites();
    const id = game.id;
    const duplicateIndex = favoriteGamesList.findIndex(gameId => gameId === id);
    if (duplicateIndex !== -1) {
      favoriteGamesList.splice(duplicateIndex, 1);
    }
    setGameFavorites(favoriteGamesList);
  }

  updateActive(timestamp) {
    this.frameRequest = requestAnimationFrame(this.updateActive);
    const deltaTime = this.frameLastTimestamp
      ? timestamp - this.frameLastTimestamp
      : 0;
    const deltaFrames = Math.floor(deltaTime / FRAME_DURATION_MS);
    // Accumulate remainder milliseconds
    this.frameLastTimestamp =
      timestamp - (deltaTime - deltaFrames * FRAME_DURATION_MS);

    if (!deltaFrames || this.props.data.loading || !this.props.data.games) {
      return;
    }

    const gameCount = this.props.data.games.games.length;
    const { active, targetActive, direction } = this.state;
    const dist = this.getTargetDistance();
    if (dist) {
      let newActive = active + direction;
      if (dist >= LUDICROUS_SPEED_TRIGGER) {
        newActive = targetActive;
      } else if (dist >= HIGH_SPEED_TRIGGER) {
        newActive = active + 2 * direction;
      }
      this.setState({
        active: (newActive + gameCount) % gameCount
      });
    }
  }

  moveLeft() {
    const gameCount = this.props.data.games.games.length;
    const { targetActive } = this.state;
    const newActive = targetActive <= 0 ? gameCount - 1 : targetActive - 1;
    const segmentWidth = SLIDER_WIDTH / (gameCount - 1);
    this.hideSortOptions();
    this.setState({
      targetActive: newActive,
      sliderPosition: Math.round(newActive * segmentWidth),
      direction: -1
    });
  }

  moveRight() {
    const gameCount = this.props.data.games.games.length;
    const { targetActive } = this.state;
    const newActive = (targetActive + 1) % gameCount;
    const segmentWidth = SLIDER_WIDTH / (gameCount - 1);
    this.hideSortOptions();
    this.setState({
      targetActive: newActive,
      sliderPosition: Math.round(newActive * segmentWidth),
      direction: 1
    });
  }

  onChange() {
    this.hideSortOptions();
  }

  searchOrderedGames = text => {
    let searchResult = [];
    const gamesList = this.props.data.games.games;

    if (gamesList) {
      searchResult = gamesList.filter(element => {
        return element.displayName.toLowerCase().includes(text.toLowerCase());
      });
    }

    this.setState({
      searchTerm: text,
      active: 0,
      targetActive: 0,
      direction: 0,
      sliderPosition: 0,
      searchFoundList: searchResult
    });
  };

  getGameList() {
    const gamesData = this.props.data.games.games;
    const { orderMethod } = this.state;
    return orderMethod ? orderMethod.sort(gamesData) : gamesData;
  }

  generateGames() {
    const { searchFoundList } = this.state;
    const favorites = getGameFavorites();
    let games;

    if (searchFoundList && searchFoundList.length === 0) {
      return (
        <swiper-container
          className="swiper-container"
          mousewheel="true"
          init="false"
        >
          <div className="search-not-found">{this.searchNotFound()}</div>
        </swiper-container>
      );
    } else if (searchFoundList && searchFoundList.length >= 5) {
      games = searchFoundList;
    } else if (
      searchFoundList &&
      searchFoundList.length < 5 &&
      searchFoundList.length >= 3
    ) {
      games = searchFoundList;
    } else if (searchFoundList && searchFoundList.length === 2) {
      games = searchFoundList;
    } else if (searchFoundList && searchFoundList.length === 1) {
      games = searchFoundList;
    } else {
      games = this.getGameList();
    }

    return (
      <swiper-container
        className="swiper-container"
        mousewheel="true"
        init="false"
      >
        {games?.map((game, index) => {
          const isFavorite = favorites.includes(game.id);
          return (
            <swiper-slide key={`${game.id}-${index}`}>
              <CSSTransition
                in={true}
                appear={true}
                timeout={150}
                unmountOnExit
                classNames={'cover'}
              >
                <GameCard
                  key={index}
                  game={game}
                  isFavorite={isFavorite}
                  onFavorite={this.addGameToFavorites}
                  onRemoveFavorite={this.removeGameFromFavorites}
                  isClickDisabled={this.state.disableClick}
                />
              </CSSTransition>
            </swiper-slide>
          );
        })}
      </swiper-container>
    );
  }

  searchNotFound() {
    return (
      <div className="empty-games-list-container">
        <span>No games match that search.</span>
        <TextButton
          className="btn clear-search button-color-green"
          onClick={this.handleClearSearch}
        >
          Clear Search
        </TextButton>
      </div>
    );
  }

  renderSortOptions() {
    const { orderMethod } = this.state;
    const options = this.gameSortingOptions.map((option, index) => {
      return (
        <ListGroupItem
          key={index}
          className={`sort-option-item ${
            option.name === orderMethod.name ? 'active' : ''
          }`}
          target={option}
          onClick={() => this.handleSortSelection(option)}
        >
          <img
            src={
              option.name === orderMethod.name
                ? SelectedFilterCheck
                : UnselectedFilterCheck
            }
            alt={'Selected'}
          />
          <span>{option.name}</span>
        </ListGroupItem>
      );
    });

    return (
      <div className="sort-options-container">
        <ListGroup className="sort-group">{options}</ListGroup>
      </div>
    );
  }

  renderSearchBox() {
    return (
      <DebouncedTextInput
        className="search-bar-pop-up"
        placeholder="Search For Game"
        autoFocus
        defaultValue={this.state.searchTerm}
        onChange={this.searchOrderedGames}
      />
    );
  }

  renderExitIcon() {
    if (this.state.searchTerm) {
      return (
        <TextButton
          className="search-pop-up-clear-btn"
          onClick={this.handleClearSearch}
        >
          <A11ySvg component={ExitIcon} alt="Clear Search" />
        </TextButton>
      );
    } else {
      return (
        <TextButton
          className="search-pop-up-clear-btn"
          onClick={this.onHideSearch}
        >
          <A11yFontIcon icon="chevron-left" alt="Close Search" />
        </TextButton>
      );
    }
  }

  renderSearchSortingButtons() {
    const { showSearchBox, showSortOptions } = this.state;
    const className = `search-sorting-container ${
      showSearchBox ? ' hide-container' : ''
    }`;
    return (
      <div className={className}>
        <TextButton
          className="button-color-green carousel-search-btn"
          onClick={
            this.state.showSearchBox ? this.onHideSearch : this.showSearch
          }
        >
          <img src={IconSearch} alt="Search Games" />
        </TextButton>
        {this.state.showSearchBox && this.renderSearchBox()}
        <TextButton
          className="carousel-sort-btn button-color-green"
          onClick={
            this.state.showSortOptions
              ? this.hideSortOptions
              : this.showSortOptions
          }
        >
          <img src={IconFilter} alt="Search Games" />
        </TextButton>
        {showSortOptions && this.renderSortOptions()}
      </div>
    );
  }

  render() {
    const { data, layoutEnabled } = this.props;
    const { searchTerm, searchFoundList } = this.state;

    if (data.loading || layoutEnabled.loading) {
      return null;
    }

    if (data.error || layoutEnabled.error) {
      return (
        <div className="alert alert-danger">
          There was a problem retrieving the games carousel. Please try again
          later.
        </div>
      );
    }

    const newLayoutEnabled = layoutEnabled?.remoteConfigEntry?.value;
    const disabled = searchFoundList && searchFoundList.length < 2;
    const emptySearchList = searchFoundList && searchFoundList.length === 0;
    const targetDistance = this.getTargetDistance();
    const speedClass =
      targetDistance >= LUDICROUS_SPEED_TRIGGER
        ? 'ludicrous-speed'
        : targetDistance >= HIGH_SPEED_TRIGGER
        ? 'high-speed'
        : 'normal-speed';

    return newLayoutEnabled ? (
      <div className="new-game-slots-page">
        <GamePageTitle onChangeFunction={this.handleSearchGame} />
        <GamePageNavigationBar
          games={data?.games?.games}
          searchTerm={searchTerm}
          onFavorite={this.addGameToFavorites}
          onRemoveFavorite={this.removeGameFromFavorites}
        />
      </div>
    ) : (
      <div className="game-slots-page">
        <PageTitleBar title="Games" icon={GamesIcon} />
        <Helmet>
          <title>Games</title>
        </Helmet>
        <div className="light-ray-container" />
        {this.renderSearchSortingButtons()}
        <TransitionGroup className={`game-cards ${speedClass}`}>
          {this.generateGames()}
          <TextButton
            onClick={this.moveLeft}
            className={`carousel-btn btn-left${
              !emptySearchList ? '' : ' hidden'
            }`}
            disabled={disabled}
          >
            <img src={leftArrow} alt="Left-Arrow" />
          </TextButton>
          <TextButton
            onClick={this.moveRight}
            className={`carousel-btn btn-right${
              !emptySearchList ? '' : ' hidden'
            }`}
            disabled={disabled}
          >
            <img src={rightArrow} alt="Right-Arrow" />
          </TextButton>
        </TransitionGroup>
        <div
          className={`slider-container`}
          style={{ visibility: !emptySearchList ? 'inherit' : 'hidden' }}
        >
          <input
            type="range"
            min={0}
            max={SLIDER_WIDTH}
            className="slider"
            onChange={this.onChange}
          />
        </div>
      </div>
    );
  }
}

function orderByFavorites(games, favoriteIds) {
  const favoriteGames = favoriteIds
    .map(favoriteId => games.find(game => game.id === favoriteId))
    .filter(game => !!game);
  const otherGames = games.filter(game => !favoriteIds.includes(game.id));
  return promoteGamesInList(
    favoriteGames.concat(otherGames),
    Math.max(favoriteGames.length, 30)
  );
}

function orderByAlphabetical(games) {
  return games.slice().sort(alphabeticalSort);
}

export function orderByNewlyReleased(games) {
  return promoteGamesInList(games);
}

export function orderByRecentlyPlayed(games) {
  return promoteGamesInList(orderGamesByHistory(games));
}

GameSlotsPage.propTypes = {
  data: PropTypes.shape({
    games: PropTypes.shape({
      games: PropTypes.arrayOf(PropTypes.object)
    }),
    loading: PropTypes.bool,
    error: errorShape
  }),
  layoutEnabled: PropTypes.shape({
    remoteConfigEntry: PropTypes.shape({
      value: PropTypes.bool
    }),
    loading: PropTypes.bool,
    error: errorShape
  })
};

const REMOTE_CONFIG_EVENT = gql`
  subscription remoteConfigEvent {
    remoteConfigEvent {
      action
      entries {
        key
        value
      }
    }
  }
`;

export default compose(
  withMediaQuery(LG_BREAKPOINT_AND_UP),
  graphql(QUERY_GET_REMOTE_CONFIG_ENTRY, {
    name: 'layoutEnabled',
    options: {
      variables: {
        key: CV_WEB_GAME_LAYOUT_UPDATE_ENABLED
      },
      fetchPolicy: 'cache-and-network',
      context: {
        //TODO: remove once LEGACY FEED and casinoverse file is removed
        headers: {
          'X-WCC-Is-Casinoverse': true
        }
      }
    },
    props: ({ layoutEnabled }) => {
      return {
        layoutEnabled,
        subscribeToNewData: () => {
          return layoutEnabled.subscribeToMore({
            document: REMOTE_CONFIG_EVENT,
            updateQuery: (prev, { subscriptionData }) => {
              if (subscriptionData?.data?.action === 'Update') {
                subscriptionData?.data?.entries.forEach(({ key, value }) => {
                  if (key === CV_WEB_GAME_LAYOUT_UPDATE_ENABLED) {
                    prev.remoteConfigEntry.value = value;
                  }
                });
              }
              return prev;
            }
          });
        }
      };
    }
  }),
  graphql(GET_GAMES_QUERY, {
    props: ({ ownProps, data }) => ({
      data,
      subscribeToGameUpdates: () => {
        return data.subscribeToMore({
          document: SUBSCRIPTION_GAME_UDPATES,
          updateQuery: (prev, { subscriptionData }) => {
            if (!subscriptionData) {
              return null;
            }

            let result = cloneDeep(prev);
            const { game, removeGameId } = subscriptionData.data;
            const gameList = result.games.games;

            if (game) {
              const gameIndex = gameList.findIndex(
                oldGame => game.id === oldGame.id
              );
              gameList.splice(gameIndex, 1, game);
            }

            if (removeGameId) {
              const gameIndex = gameList.findIndex(
                game => removeGameId.toString() === game.id
              );
              gameList.splice(gameIndex, 1);
            }

            return result;
          }
        });
      }
    })
  })
)(GameSlotsPage);
